import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { useApp, TaskCategory } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { notificationService } from '@/services/notificationService';
import { toast } from 'sonner';

type ReminderTime = 'none' | '5min' | '15min' | '30min' | '1hour' | '1day';

interface Task {
  id: string;
  title: string;
  description: string;
  category: TaskCategory;
  dueDate?: Date;
  isRecurring: boolean;
  completed: boolean;
  createdAt: Date;
}

type TaskFormData = Omit<Task, 'id' | 'createdAt'>;

interface TaskFormProps {
  task?: Task;
  onSuccess: () => void;
}

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string(),
  category: z.enum(['Personal', 'Work', 'Shopping', 'Health', 'Other'] as const),
  dueDate: z.date().optional(),
  isRecurring: z.boolean(),
  completed: z.boolean(),
});

const reminderTimeToMinutes: Record<ReminderTime, number | null> = {
  'none': null,
  '5min': 5,
  '15min': 15,
  '30min': 30,
  '1hour': 60,
  '1day': 24 * 60,
};

const TaskForm: React.FC<TaskFormProps> = ({ task, onSuccess }) => {
  const { addTask, updateTask } = useApp();
  const [reminderTime, setReminderTime] = useState<ReminderTime>('none');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: task?.title || '',
      description: task?.description || '',
      category: task?.category || 'Personal',
      dueDate: task?.dueDate,
      isRecurring: task?.isRecurring || false,
      completed: task?.completed || false,
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const taskData: TaskFormData = {
      title: values.title,
      description: values.description,
      category: values.category,
      dueDate: values.dueDate,
      isRecurring: values.isRecurring,
      completed: values.completed,
    };

    if (task) {
      await updateTask(task.id, taskData);
    } else {
      await addTask(taskData);
    }

    // Request notification permission if reminder is set and due date exists
    const reminderMinutes = reminderTimeToMinutes[reminderTime];
    if (reminderMinutes !== null && values.dueDate instanceof Date) {
      try {
        const permission = await notificationService.requestPermission();
        if (permission) {
          await notificationService.scheduleNotification(
            task?.id || crypto.randomUUID(),
            values.title,
            values.description,
            values.dueDate,
            reminderMinutes
          );
          toast.success('Reminder set successfully');
        } else {
          toast.error('Notification permission denied');
        }
      } catch (error) {
        console.error('Error setting reminder:', error);
        toast.error('Failed to set reminder');
      }
    }

    onSuccess();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Work">Work</SelectItem>
                  <SelectItem value="Shopping">Shopping</SelectItem>
                  <SelectItem value="Health">Health</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dueDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Due Date</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  {...field}
                  value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                  onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isRecurring"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Recurring Task</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel>Set Reminder</FormLabel>
          <Select value={reminderTime} onValueChange={(value: ReminderTime) => setReminderTime(value)}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select reminder time" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">No reminder</SelectItem>
              <SelectItem value="5min">5 minutes before</SelectItem>
              <SelectItem value="15min">15 minutes before</SelectItem>
              <SelectItem value="30min">30 minutes before</SelectItem>
              <SelectItem value="1hour">1 hour before</SelectItem>
              <SelectItem value="1day">1 day before</SelectItem>
            </SelectContent>
          </Select>
        </FormItem>

        <Button type="submit" className={cn("w-full", task ? "bg-yellow-500" : "bg-green-500")}>
          {task ? 'Update Task' : 'Create Task'}
        </Button>
      </form>
    </Form>
  );
};

export default TaskForm; 